import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useContent } from "../content-context";
import { Eyebrow, Reveal } from "./Reveal";

function Lightbox({ index, onClose, onNav }) {
  const { gallery } = useContent();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const img = gallery[index];
  return (
    <motion.div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/95 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        aria-label="Close gallery"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center border border-ivory/30 text-ivory transition-colors hover:border-gold hover:text-gold-2"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        data-testid="lightbox-prev"
        aria-label="Previous image"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-ivory/30 text-ivory transition-colors hover:border-gold hover:text-gold-2 md:left-8"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid="lightbox-next"
        aria-label="Next image"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-ivory/30 text-ivory transition-colors hover:border-gold hover:text-gold-2 md:right-8"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <motion.figure
        key={index}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="max-h-[85vh] max-w-[88vw] md:max-w-[76vw]"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      >
        <img src={img.src} alt={img.alt} className="max-h-[78vh] w-full object-contain" />
        <figcaption className="mt-4 text-center text-[11px] uppercase tracking-widest2 text-ivory/60">
          {img.alt} — {index + 1} / {gallery.length}
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

export default function Gallery() {
  const { gallery } = useContent();
  const [open, setOpen] = useState(null);

  const nav = useCallback(
    (dir) => setOpen((i) => (i === null ? null : (i + dir + gallery.length) % gallery.length)),
    []
  );

  return (
    <section id="gallery" data-testid="gallery-section" className="bg-ivory py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="mb-14 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <Eyebrow>Gallery</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="gallery-heading">
                The island, <em className="text-gold">framed</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm font-light leading-relaxed text-navy/70">
              Click any photograph to view it full screen.
            </p>
          </Reveal>
        </div>

        <div className="grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[190px] md:gap-4 lg:auto-rows-[225px] lg:grid-cols-4" data-testid="gallery-grid">
          {gallery.map((img, i) => (
            <Reveal key={img.src + i} className={img.span} delay={(i % 4) * 0.05}>
              <button
                data-testid={`gallery-image-${i + 1}`}
                onClick={() => setOpen(i)}
                aria-label={`Open image: ${img.alt}`}
                className="group relative h-full w-full overflow-hidden"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && <Lightbox index={open} onClose={() => setOpen(null)} onNav={nav} />}
      </AnimatePresence>
    </section>
  );
}
